import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../api";

const ROLES = ["owner", "admin_operasional", "kasir", "supir"];
const roleLabels = { owner: "Owner/Pemilik", admin_operasional: "Admin Operasional", kasir: "Kasir/Keuangan", supir: "Supir" };
const roleBadge = {
  owner: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  admin_operasional: "bg-blue-100 text-blue-800 border border-blue-200",
  kasir: "bg-amber-100 text-amber-800 border border-amber-200",
  supir: "bg-violet-100 text-violet-800 border border-violet-200",
};

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "admin_operasional", Nama_Supir: "" });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [supirList, setSupirList] = useState([]);
  const [supirDialog, setSupirDialog] = useState(null);
  const [supirPick, setSupirPick] = useState("");

  const load = () => { api.get("/users").then(({ data }) => setUsers(data)).catch(() => setUsers([])); };
  useEffect(() => {
    load();
    api.get("/supir").then(({ data }) => setSupirList(data)).catch(() => setSupirList([]));
  }, []);

  const addUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success(`Akun ${form.email} dibuat sebagai ${roleLabels[form.role] || form.role}`);
      setForm({ name: "", email: "", password: "", role: "admin_operasional", Nama_Supir: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (u, role, namaSupir) => {
    if (role === "supir" && !namaSupir && !u.Nama_Supir) {
      setSupirPick("");
      setSupirDialog(u);
      return;
    }
    try {
      const payload = { role };
      if (namaSupir) payload.Nama_Supir = namaSupir;
      await api.put(`/users/${u.id}`, payload);
      toast.success(`Role ${u.email} diubah menjadi ${roleLabels[role] || role}`);
      setSupirDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success(`Akun ${deleteTarget.email} dihapus`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="users-page" className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900">Manajemen User</h1>
        <p className="text-sm text-zinc-500 mt-1">Kelola akun dan role: Owner/Pemilik (akses penuh), Admin Operasional (kelola trip), Kasir/Keuangan (uang jalan &amp; gaji), Supir (input tonase trip miliknya)</p>
      </div>

      <Card className="border-zinc-200 shadow-sm">
        <CardHeader className="pb-4"><CardTitle className="text-base font-semibold tracking-tight">Tambah Akun Baru</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={addUser} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
            <div className="space-y-1.5">
              <Label>Nama</Label>
              <Input data-testid="user-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nama lengkap" className="bg-white" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input data-testid="user-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@trip.co.id" className="bg-white" />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <Input data-testid="user-password-input" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min. 6 karakter" className="bg-white" />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {ROLES.map((r) => <SelectItem key={r} value={r} data-testid={`role-option-${r}`}>{roleLabels[r]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.role === "supir" && (
              <div className="space-y-1.5">
                <Label>Nama Supir (Master)</Label>
                <Select value={form.Nama_Supir} onValueChange={(v) => setForm({ ...form, Nama_Supir: v })} required>
                  <SelectTrigger data-testid="user-nama-supir-select" className="bg-white"><SelectValue placeholder="Pilih supir" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {supirList.map((s) => (
                      <SelectItem key={s.id} value={s.Nama_Supir} data-testid={`supir-option-${s.Nama_Supir.replace(/ /g, "-")}`}>
                        {s.Nama_Supir}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button data-testid="user-add-button" type="submit" disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800 text-white transition-colors duration-200">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Tambah
            </Button>
          </form>
        </CardContent>
      </Card>

      <div className="bg-white border border-zinc-200 rounded-lg shadow-sm overflow-hidden">
        {users === null ? (
          <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  {["Nama", "Email", "Role", "Ubah Role", "Aksi"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-200">
                    <td className="px-4 py-3 font-medium text-zinc-900 whitespace-nowrap">{u.name}{u.id === me.id && <span className="ml-2 text-xs text-zinc-400">(Anda)</span>}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{u.email}</td>
                    <td className="px-4 py-3 whitespace-nowrap"><Badge className={`${roleBadge[u.role] || ""} font-medium`}>{roleLabels[u.role] || u.role}</Badge>{u.role === "supir" && u.Nama_Supir && <span className="ml-2 text-xs text-zinc-500">→ {u.Nama_Supir}</span>}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={u.id === me.id}>
                        <SelectTrigger data-testid={`change-role-${u.email}`} className="bg-white w-36 h-8"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabels[r]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button data-testid={`delete-user-${u.email}`} onClick={() => setDeleteTarget(u)} disabled={u.id === me.id}
                        className="p-2 rounded-md text-zinc-500 hover:text-red-600 hover:bg-red-50 transition-colors duration-200 disabled:opacity-30 disabled:pointer-events-none">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AlertDialog open={!!supirDialog} onOpenChange={(open) => !open && setSupirDialog(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Hubungkan ke Master Supir</AlertDialogTitle>
            <AlertDialogDescription>
              Pilih nama supir dari Master Data untuk akun {supirDialog?.email}. Supir hanya akan melihat trip miliknya sendiri.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Select value={supirPick} onValueChange={setSupirPick}>
            <SelectTrigger data-testid="dialog-nama-supir-select" className="bg-white"><SelectValue placeholder="Pilih supir" /></SelectTrigger>
            <SelectContent className="bg-white">
              {supirList.map((s) => <SelectItem key={s.id} value={s.Nama_Supir}>{s.Nama_Supir}</SelectItem>)}
            </SelectContent>
          </Select>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-supir-link">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-supir-link" disabled={!supirPick}
              onClick={() => changeRole(supirDialog, "supir", supirPick)}
              className="bg-emerald-700 hover:bg-emerald-800">Simpan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Akun?</AlertDialogTitle>
            <AlertDialogDescription>Akun {deleteTarget?.email} akan dihapus permanen.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-user">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-user" onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
